import type { AppState, CapacityUnit, CollectionName, ContentItem, DailyRecord, Decision, Lead, Offer, Order } from '../types'
import { CONTENT_STATES, ORDER_STATES, STATUS_LIST } from '../domain/constants'
import { capacityStats, gateEvidence, money, nextAction, num, orderCalc, pct } from '../domain/calculations'
import { createId, initialState, todayISO } from '../domain/seed'
import { normalizeState } from '../domain/validation'
import { downloadBackup, parseAndValidateBackup } from '../backup/backup'
import { createAutomaticBackup, listAutomaticBackups, listRevisions, loadState, recordRevision, saveState } from '../storage/localStore'
import { logger } from '../logging/logger'
import { TrevahhhSyncEngine } from '../sync/syncEngine'
import { createOperationsClient, type OperationsOverview, type OperationsTask } from '../ops/opsClient'
import { CodexFuelController } from '../codexFuel'

type ModalSaveHandler = (() => void) | null
type ViewName = 'command' | 'sprint' | 'capacity' | 'offers' | 'leads' | 'orders' | 'content' | 'decisions' | 'codex-fuel' | 'settings'

const viewLabels: Record<ViewName, string> = {
  command: 'Founder Command',
  sprint: '14-Day Sprint',
  capacity: 'Capacity Lab',
  offers: 'Offer Engine',
  leads: 'Lead Pipeline',
  orders: 'Orders + Cash',
  content: 'Content Engine',
  decisions: 'Decision Log',
  'codex-fuel': 'Codex Fuel',
  settings: 'Settings'
}

const escapeHtml = (value: unknown): string =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char] ?? char)

const optionList = (items: readonly string[], selected: string): string =>
  items.map((item) => `<option value="${escapeHtml(item)}" ${item === selected ? 'selected' : ''}>${escapeHtml(item)}</option>`).join('')

export class LaunchCommandCenter {
  private state: AppState = loadState()
  private activeView: ViewName = 'command'
  private modalSaveHandler: ModalSaveHandler = null
  private timerHandle: number | null = null
  public sync: TrevahhhSyncEngine
  public codexFuel: CodexFuelController
  private ops = createOperationsClient()
  private opsStatus: { state: 'checking' | 'online' | 'offline'; detail: string; overview: OperationsOverview | null } = {
    state: 'checking',
    detail: 'Local operations server not checked yet.',
    overview: null
  }

  constructor(private root: HTMLElement) {
    this.sync = new TrevahhhSyncEngine({
      getState: () => this.state,
      applyRemoteState: (state) => this.applyRemoteState(state),
      onStatus: () => this.handleSyncStatusChanged()
    })
    this.codexFuel = new CodexFuelController(this.sync, () => this.renderCodexFuelSurfaces())
  }

  async mount(): Promise<void> {
    this.root.innerHTML = this.shell()
    this.bindEvents()
    this.renderAll()
    void this.refreshOperationsStatus()
    await this.registerServiceWorker()
    await this.sync.init().catch((error: unknown) => {
      logger.error('Sync initialization failed', { error: error instanceof Error ? error.message : String(error) })
      this.sync.updateStatus('error', error instanceof Error ? error.message : 'Sync initialization failed')
    })
    await this.codexFuel.load().catch((error: unknown) => this.codexFuel.handleError(error))
  }

  private shell(): string {
    return `
      <div class="app-shell">
        <aside class="sidebar" aria-label="Primary navigation">
          <div class="brand"><h1><span>Trevahhh</span> OS</h1><p>Founder Command System</p></div>
          <div class="gate-mini"><div class="eyebrow">Current gate</div><strong>Prove ten units. Close one profitable paid pilot.</strong><div class="progress"><i id="sideProgress"></i></div></div>
          <nav class="nav" id="nav">${this.navButtons()}</nav>
          <div class="side-actions">
            <button class="btn primary" data-action="one-hour"><span>Run One-Hour Mode</span><b>60</b></button>
            <button class="btn ghost" data-action="export">Export Backup</button>
          </div>
        </aside>
        <main class="main">
          <header class="topbar">
            <div><strong id="topTitle">Founder Command</strong><div class="date" id="todayLabel"></div></div>
            <div class="right">
              <button class="badge" id="syncBadge" type="button" data-action="sync-modal" aria-label="Open cloud sync status"><i class="dot"></i> Cloud sync</button>
              <button class="btn primary" data-action="one-hour">One-Hour Mode</button>
            </div>
          </header>
          <section class="jay-task-surface" id="jayTaskSurface" aria-live="polite" aria-label="Jay task queue"></section>
          <div class="content">
            <section class="view active" id="view-command"></section>
            <section class="view" id="view-sprint"></section>
            <section class="view" id="view-capacity"></section>
            <section class="view" id="view-offers"></section>
            <section class="view" id="view-leads"></section>
            <section class="view" id="view-orders"></section>
            <section class="view" id="view-content"></section>
            <section class="view" id="view-decisions"></section>
            <section class="view" id="view-codex-fuel"></section>
            <section class="view" id="view-settings"></section>
          </div>
        </main>
      </div>
      <div class="modal-backdrop" id="modalBackdrop" role="dialog" aria-modal="true" aria-labelledby="modalTitle">
        <div class="modal">
          <div class="modal-head"><h3 id="modalTitle">Modal</h3><button class="close" data-action="close-modal" aria-label="Close">x</button></div>
          <div class="modal-body" id="modalBody"></div>
          <div class="modal-foot" id="modalFoot"></div>
        </div>
      </div>
      <input id="importFile" type="file" accept="application/json,.json" hidden />`
  }

  private navButtons(): string {
    const icons: Record<ViewName, string> = { command: '01', sprint: '14', capacity: 'CL', offers: 'OF', leads: 'LP', orders: '$', content: 'CE', decisions: 'DL', 'codex-fuel': 'CF', settings: 'ST' }
    return (Object.keys(viewLabels) as ViewName[]).map((view) => `<button type="button" aria-label="${escapeHtml(viewLabels[view])}" class="${view === this.activeView ? 'active' : ''}" data-view="${view}"><span class="icon">${icons[view]}</span><span>${viewLabels[view]}</span></button>`).join('')
  }

  private bindEvents(): void {
    this.root.addEventListener('click', (event) => this.handleClick(event))
    this.root.addEventListener('change', (event) => this.handleChange(event))
    this.root.addEventListener('input', (event) => this.handleInput(event))
    document.getElementById('importFile')?.addEventListener('change', (event) => void this.importData(event))
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeModal()
    })
  }

  private handleClick(event: Event): void {
    const target = event.target as HTMLElement
    const viewButton = target.closest<HTMLButtonElement>('[data-view]')
    if (viewButton?.dataset.view) {
      this.switchView(viewButton.dataset.view as ViewName)
      return
    }
    const actionButton = target.closest<HTMLElement>('[data-action]')
    const action = actionButton?.dataset.action
    if (!action) return
    const id = actionButton.dataset.id ?? actionButton.dataset.deviceId
    void this.performAction(action, id)
  }

  private handleChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    if (target.dataset.update) {
      this.updateField(target.dataset.update, target.dataset.id ?? '', target.dataset.key ?? '', target.value)
    }
  }

  private handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    if (target.id === 'leadSearch' || target.id === 'leadFilter') this.renderLeads()
  }

  private async performAction(action: string, id?: string): Promise<void> {
    switch (action) {
      case 'one-hour': this.openOneHour(); break
      case 'close-modal': this.closeModal(); break
      case 'modal-save': this.modalSaveHandler?.(); break
      case 'mark-today': this.markToday(); break
      case 'next-action': this.goToNextAction(); break
      case 'export': downloadBackup(this.state); break
      case 'import': document.getElementById('importFile')?.click(); break
      case 'reset': this.resetAll(); break
      case 'reset-sprint': this.resetSprint(); break
      case 'add-capacity': this.addCapacityUnit(); break
      case 'delete-capacity': this.deleteItem('capacity', id); break
      case 'add-offer': this.openOfferModal(); break
      case 'edit-offer': this.openOfferModal(this.state.offers.find((item) => item.id === id)); break
      case 'add-lead': this.openLeadModal(); break
      case 'delete-lead': this.deleteItem('leads', id); break
      case 'add-order': this.openOrderModal(); break
      case 'delete-order': this.deleteItem('orders', id); break
      case 'add-content': this.openContentModal(); break
      case 'delete-content': this.deleteItem('content', id); break
      case 'add-decision': this.openDecisionModal(); break
      case 'delete-decision': this.deleteItem('decisions', id); break
      case 'save-settings': this.saveSettings(); break
      case 'sync-modal': this.openSyncModal(); break
      case 'save-sync-config': await this.saveSyncConfiguration(); break
      case 'send-magic-link': await this.syncAuth('magic'); break
      case 'password-sign-in': await this.syncAuth('login'); break
      case 'password-sign-up': await this.syncAuth('signup'); break
      case 'sync-now': await this.sync.flush(); await this.sync.pullRemote(); this.openSyncModal(); break
      case 'merge-local': this.sync.queueEntireState(this.state); await this.sync.flush(); this.openSyncModal(); break
      case 'sign-out': await this.sync.signOut(); this.openSyncModal(); break
      case 'save-device': this.sync.saveDeviceName((document.getElementById('deviceName') as HTMLInputElement | null)?.value ?? ''); this.openSyncModal(); break
      case 'conflict-local': if (id) await this.sync.resolveConflict(id, 'local'); this.openSyncModal(); break
      case 'conflict-remote': if (id) await this.sync.resolveConflict(id, 'remote'); this.openSyncModal(); break
      case 'show-backups': this.openBackupsModal(); break
      case 'show-revisions': this.openRevisionsModal(); break
      case 'refresh-ops': await this.refreshOperationsStatus(); break
      case 'complete-jay-task': if (id) await this.completeJayTask(id); break
      case 'approve-ops': if (id) await this.respondOperationsApproval(id, 'approved'); break
      case 'reject-ops': if (id) await this.respondOperationsApproval(id, 'rejected'); break
      case 'start-ops-preset': if (id) await this.startOperationsPreset(id); break
      case 'cancel-ops-job': if (id) await this.cancelOperationsJob(id); break
      case 'view-ops-artifact': if (id) await this.openOperationsArtifact(id); break
      case 'download-ops-artifact': if (id) await this.downloadOperationsArtifact(id); break
      case 'artifact-library': this.openArtifactLibraryModal(); break
      case 'manage-project-commands': if (id) this.openProjectCommandsModal(id); break
      case 'save-project-commands': if (id) await this.saveProjectCommands(id); break
      case 'start-project-command': if (id) await this.startProjectCommand(id); break
      case 'signing-instructions': this.openSigningInstructionsModal(id); break
      case 'codex-pair': await this.codexFuel.createPairingCode().catch((error: unknown) => alert(error instanceof Error ? error.message : String(error))); break
      case 'codex-revoke': if (id && confirm('Revoke this Codex Fuel bridge device?')) await this.codexFuel.revokeDevice(id).catch((error: unknown) => alert(error instanceof Error ? error.message : String(error))); break
      case 'codex-save-prefs': await this.codexFuel.savePreferences().catch((error: unknown) => alert(error instanceof Error ? error.message : String(error))); break
      case 'start-timer': this.startTimer(); break
      default: break
    }
  }

  private save(reason = 'autosave'): void {
    this.state = normalizeState(this.state)
    createAutomaticBackup(this.state, reason)
    saveState(this.state)
    this.renderAll()
    if (this.sync.initialized) this.sync.onLocalStateChanged(this.state)
  }

  private applyRemoteState(remoteState: AppState): void {
    createAutomaticBackup(this.state, 'before-remote-apply')
    this.state = normalizeState(remoteState)
    saveState(this.state)
    this.renderAll()
  }

  private renderAll(): void {
    this.renderCommand()
    this.renderSprint()
    this.renderCapacity()
    this.renderOffers()
    this.renderLeads()
    this.renderOrders()
    this.renderContent()
    this.renderDecisions()
    this.renderCodexFuelSurfaces()
    this.renderSettings()
    this.renderSyncSurfaces()
    this.renderOperationsSurfaces()
    const today = document.getElementById('todayLabel')
    if (today) today.textContent = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  }

  private renderCommand(): void {
    const container = document.getElementById('view-command')
    if (!container) return
    const stats = capacityStats(this.state)
    const advanced = this.state.leads.filter((lead) => lead.status !== 'Not Contacted').length
    const pocket = this.state.orders.reduce((total, order) => total + orderCalc(order, this.state).pocket, 0)
    const evidence = gateEvidence(this.state)
    const done = evidence.filter((item) => item.done).length
    const progress = Math.round(done / evidence.length * 100)
    const next = nextAction(this.state)
    const revenue = advanced < 10 ? 'Contact the next ranked prospect and reach the purchasing authority.' : 'Advance the hottest lead toward a paid pilot deposit.'
    const production = stats.attempted < 10 ? 'Make and log the next representative unit.' : 'Select the three strongest sales-ready samples.'
    const system = stats.attempted < 10 ? 'Record every minute, defect, and material cost while the work happens.' : 'Replace starter pricing assumptions with measured economics.'
    container.innerHTML = `
      <div class="pagehead"><div><h2>Founder Command</h2><p>The operating screen answers one question: what moves the gate next?</p></div><button class="btn green" data-action="mark-today">Record Today's Result</button></div>
      <div class="grid kpis">
        ${this.kpi('Units attempted', String(stats.attempted), '10 required for capacity proof', 'gold')}
        ${this.kpi('Sellable units', String(stats.sellable), stats.attempted ? `${pct(stats.defect)} defect rate` : 'Defect rate not measured', 'green')}
        ${this.kpi('Prospects advanced', String(advanced), 'Beyond Not Contacted', 'blue')}
        ${this.kpi('Pocket cash', money(pocket), 'After reserve, not gross revenue')}
      </div>
      <div class="card codex-compact" id="codexFuelCompact" style="margin-top:16px">${this.codexFuel.compactHtml()}</div>
      <div class="card section hero-gate">
        <div class="eyebrow">Single current gate</div>
        <h3>Complete ten measured double-sided birch cards, calculate real economics, then secure one paid Long Beach B2B pilot.</h3>
        <div class="gate-row"><div class="big-progress"><div class="meta"><span>${done} of ${evidence.length} evidence requirements complete</span><span>${progress}%</span></div><div class="progress"><i style="width:${progress}%"></i></div></div><button class="btn primary" data-action="next-action">Execute Next Action</button></div>
      </div>
      <div class="layout2">
        <div class="card section"><h3>Today's Three Moves</h3><p class="desc">One revenue action. One production action. One system action.</p><div class="today-list">
          ${this.actionRow('1', 'Revenue', revenue, '25 min')}
          ${this.actionRow('2', 'Production', production, '25 min')}
          ${this.actionRow('3', 'System', system, '10 min')}
        </div></div>
        <div class="card section"><h3>Gate Evidence</h3><p class="desc">${escapeHtml(next.type)}: ${escapeHtml(next.text)}</p><div class="checks">
          ${evidence.map((item) => `<label class="check"><input type="checkbox" ${item.done ? 'checked' : ''} ${'manual' in item ? `data-update="manualChecks" data-key="${item.manual}"` : 'disabled'} /><span>${escapeHtml(item.label)}</span><span class="pill ${item.done ? 'done' : 'open'}">${item.done ? 'Complete' : 'Open'}</span></label>`).join('')}
        </div></div>
      </div>
      <div class="layout2">
        <div class="card section"><h3>Lead Pipeline</h3><p class="desc">The next ten contacts, not a vanity CRM.</p><div class="chart">${this.pipelineChart()}</div></div>
        <div class="card section"><h3>Do Not Start</h3><p class="desc">These are distraction controls.</p><div class="notice gold">No paid ads. No premium packaging. No speculative inventory. No giant website build. No new machinery. No public Trevahhh SaaS. No scaling third-party character artwork.</div><div class="quote">Jay creates the vision. Systems, AI, equipment, and eventually a team perform the recurring execution.</div></div>
      </div>`
    const sideProgress = document.getElementById('sideProgress')
    if (sideProgress) sideProgress.style.width = `${progress}%`
  }

  private kpi(label: string, value: string, sub: string, tone = ''): string {
    return `<div class="card kpi ${tone}"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value)}</div><div class="sub">${escapeHtml(sub)}</div></div>`
  }

  private actionRow(numValue: string, title: string, text: string, pill: string): string {
    return `<div class="action"><div class="num">${numValue}</div><div><strong>${escapeHtml(title)}</strong><small>${escapeHtml(text)}</small></div><span class="pill open">${escapeHtml(pill)}</span></div>`
  }

  private pipelineChart(): string {
    const counts = STATUS_LIST.map((status) => ({ status, count: this.state.leads.filter((lead) => lead.status === status).length })).filter((row) => ['Not Contacted', 'Connected', 'Demo Scheduled', 'Quoted', 'Negotiating', 'Won'].includes(row.status))
    const max = Math.max(1, ...counts.map((row) => row.count))
    return counts.map((row) => `<div class="bar" style="height:${Math.max(7, row.count / max * 100)}%"><b>${row.count}</b><span>${escapeHtml(row.status.replace('Not Contacted', 'Open').replace('Demo Scheduled', 'Demo'))}</span></div>`).join('')
  }

  private renderSprint(): void {
    const complete = this.state.sprint.filter((item) => item.status === 'Complete').length
    this.setView('sprint', `
      <div class="pagehead"><div><h2>14-Day Launch Sprint</h2><p>Every day produces evidence, money, buyer conversation, or reusable capability.</p></div><button class="btn primary" data-action="reset-sprint">Reset Sprint</button></div>
      ${this.metricStrip([['Complete', complete], ['In progress', this.state.sprint.filter((item) => item.status === 'In Progress').length], ['Blocked', this.state.sprint.filter((item) => item.status === 'Blocked').length], ['Planned hours', this.state.sprint.reduce((sum, item) => sum + num(item.hours), 0)], ['Evidence', this.state.sprint.filter((item) => item.evidence.trim()).length], ['Movement', `${Math.round(complete / 14 * 100)}%`]])}
      ${this.table(['Day', 'Command', 'Deliverable', 'Gate', 'Hours', 'Status', 'Evidence / Notes'], this.state.sprint.map((item) => `<tr><td><b>${item.day}</b></td><td>${escapeHtml(item.command)}</td><td>${escapeHtml(item.deliverable)}</td><td>${escapeHtml(item.gate)}</td><td>${item.hours}</td><td><select data-update="sprint" data-id="${item.id}" data-key="status">${optionList(['Not Started', 'In Progress', 'Blocked', 'Complete'], item.status)}</select></td><td><textarea data-update="sprint" data-id="${item.id}" data-key="evidence">${escapeHtml(item.evidence)}</textarea></td></tr>`).join(''))}`)
  }

  private renderCapacity(): void {
    const stats = capacityStats(this.state)
    const rows = this.state.capacity.length ? this.state.capacity.map((unit, index) => `<tr><td><b>${index + 1}</b></td>${this.inputCell('capacity', unit.id, 'date', unit.date, 'date')}${this.inputCell('capacity', unit.id, 'design', unit.design)}${this.inputCell('capacity', unit.id, 'material', unit.material, 'number', '0.01')}${this.inputCell('capacity', unit.id, 'art', unit.art, 'number')}${this.inputCell('capacity', unit.id, 'setup', unit.setup, 'number')}${this.inputCell('capacity', unit.id, 'front', unit.front, 'number')}${this.inputCell('capacity', unit.id, 'flip', unit.flip, 'number')}${this.inputCell('capacity', unit.id, 'back', unit.back, 'number')}${this.inputCell('capacity', unit.id, 'finish', unit.finish, 'number')}${this.inputCell('capacity', unit.id, 'package', unit.package, 'number')}${this.inputCell('capacity', unit.id, 'machine', unit.machine, 'number')}<td><select data-update="capacity" data-id="${unit.id}" data-key="pass">${optionList(['', 'Pass', 'Fail'], unit.pass)}</select></td>${this.inputCell('capacity', unit.id, 'defect', unit.defect)}${this.inputCell('capacity', unit.id, 'notes', unit.notes)}<td><button class="btn danger icon-only" data-action="delete-capacity" data-id="${unit.id}" aria-label="Delete capacity unit">x</button></td></tr>`).join('') : '<tr><td colspan="16" class="empty">No test units yet. Add the first representative unit and record the work while it happens.</td></tr>'
    this.setView('capacity', `
      <div class="pagehead"><div><h2>Capacity Laboratory</h2><p>Track what actually happens. Theoretical runtime is not sellable capacity.</p></div><button class="btn green" data-action="add-capacity">Add Test Unit</button></div>
      <div class="notice">Commercial artwork gate: use Jay-owned, licensed, commissioned, or customer-authorized artwork. Third-party protected artwork is blocked from scaled sales.</div>
      ${this.metricStrip([['Attempted', stats.attempted], ['Sellable', stats.sellable], ['Defect rate', stats.attempted ? pct(stats.defect) : 'Not measured'], ['Avg cash cost', stats.attempted ? money(stats.avgCash) : 'Not measured'], ['Avg economic cost', stats.attempted ? money(stats.avgEconomic) : 'Not measured'], ['Avg labor', stats.attempted ? `${Math.round(stats.avgLabor)} min` : 'Not measured']])}
      ${this.table(['#', 'Date', 'Design', 'Material $', 'Art min', 'Setup', 'Front', 'Flip', 'Back', 'Finish', 'Package', 'Machine', 'Pass?', 'Defect', 'Notes', ''], rows, 'wide')}`)
  }

  private renderOffers(): void {
    const stats = capacityStats(this.state)
    const floor = stats.avgCash ? stats.avgCash / (1 - this.state.settings.targetMargin) : 0
    this.setView('offers', `<div class="pagehead"><div><h2>Offer Engine</h2><p>Defined outcomes with price floors, rights gates, and repeat-order paths.</p></div><button class="btn green" data-action="add-offer">Add Offer</button></div><div class="cards">${this.state.offers.map((offer) => `<article class="card item-card"><div class="eyebrow">${escapeHtml(offer.status)}</div><h3>${escapeHtml(offer.name)}</h3><p>${escapeHtml(offer.line)}</p><div class="meta"><span class="pill open">${money(offer.price)}</span><span class="pill">${offer.qty} units</span><span class="pill">${escapeHtml(offer.rights)}</span></div><p><b>Buyer:</b> ${escapeHtml(offer.buyer)}<br><b>Repeat:</b> ${escapeHtml(offer.repeat)}</p>${floor && offer.price / offer.qty < floor ? `<div class="notice">Below measured unit price floor of ${money(floor)}.</div>` : ''}<div class="footer"><span class="pill ${offer.status === 'Blocked' ? 'blocked' : offer.status === 'PRIMARY' ? 'done' : 'open'}">${escapeHtml(offer.status)}</span><button class="btn" data-action="edit-offer" data-id="${offer.id}">Edit</button></div></article>`).join('')}</div>`)
  }

  private renderLeads(limit = false): void {
    const search = (document.getElementById('leadSearch') as HTMLInputElement | null)?.value.toLowerCase() ?? ''
    const filter = (document.getElementById('leadFilter') as HTMLSelectElement | null)?.value ?? ''
    let rows = this.state.leads.filter((lead) => (!search || JSON.stringify(lead).toLowerCase().includes(search)) && (!filter || lead.status === filter)).sort((a, b) => a.rank - b.rank)
    if (limit) rows = rows.filter((lead) => !['Won', 'Lost'].includes(lead.status)).slice(0, 10)
    this.setView('leads', `<div class="pagehead"><div><h2>Lead Pipeline</h2><p>Prioritize purchasing authorities with repeat demand.</p></div><button class="btn green" data-action="add-lead">Add Lead</button></div><div class="toolbar"><input id="leadSearch" placeholder="Search prospects" value="${escapeHtml(search)}" /><select id="leadFilter"><option value="">All statuses</option>${optionList(STATUS_LIST, filter)}</select></div>${this.table(['Rank', 'Business', 'Segment', 'Offer angle', 'Contact', 'Status', 'Next action', 'Next date', 'Potential', ''], rows.map((lead) => `<tr><td>${lead.rank}</td><td><b>${escapeHtml(lead.business)}</b></td><td>${escapeHtml(lead.segment)}</td><td>${escapeHtml(lead.angle)}</td><td>${escapeHtml(lead.contact)}</td><td><select data-update="leads" data-id="${lead.id}" data-key="status">${optionList(STATUS_LIST, lead.status)}</select></td>${this.inputCell('leads', lead.id, 'next', lead.next)}${this.inputCell('leads', lead.id, 'nextDate', lead.nextDate, 'date')}<td><select data-update="leads" data-id="${lead.id}" data-key="potential">${optionList(['High', 'Medium', 'Low'], lead.potential)}</select></td><td><button class="btn danger icon-only" data-action="delete-lead" data-id="${lead.id}" aria-label="Delete lead">x</button></td></tr>`).join(''))}`)
  }

  private renderOrders(): void {
    const totals = this.state.orders.reduce((acc, order) => {
      const calc = orderCalc(order, this.state)
      acc.revenue += calc.revenue
      acc.cash += calc.cash
      acc.tax += calc.tax
      acc.pocket += calc.pocket
      return acc
    }, { revenue: 0, cash: 0, tax: 0, pocket: 0 })
    const rows = this.state.orders.length ? this.state.orders.map((order) => {
      const calc = orderCalc(order, this.state)
      return `<tr><td><b>${escapeHtml(order.orderNo)}</b></td><td>${escapeHtml(order.customer)}</td><td>${escapeHtml(order.offer)}</td><td>${order.qty}</td><td>${money(calc.revenue)}</td><td>${money(calc.cash)}</td><td>${money(calc.tax)}</td><td><b class="${calc.pocket >= 0 ? 'positive' : 'negative'}">${money(calc.pocket)}</b></td><td>${order.hours}</td><td><select data-update="orders" data-id="${order.id}" data-key="status">${optionList(ORDER_STATES, order.status)}</select></td><td><select data-update="orders" data-id="${order.id}" data-key="repeat">${optionList(['No', 'Yes'], order.repeat)}</select></td><td><select data-update="orders" data-id="${order.id}" data-key="referral">${optionList(['No', 'Yes'], order.referral)}</select></td><td><button class="btn danger icon-only" data-action="delete-order" data-id="${order.id}" aria-label="Delete order">x</button></td></tr>`
    }).join('') : '<tr><td colspan="13" class="empty">No orders yet. The next target is one paid three-card B2B pilot.</td></tr>'
    this.setView('orders', `<div class="pagehead"><div><h2>Orders + Pocket Cash</h2><p>Revenue is not success. Record every cost, reserve, founder hour, and repeat opportunity.</p></div><button class="btn green" data-action="add-order">Add Order</button></div>${this.metricStrip([['Gross revenue', money(totals.revenue)], ['Cash cost', money(totals.cash)], ['Tax reserve', money(totals.tax)], ['Pocket cash', money(totals.pocket)], ['Complete', this.state.orders.filter((item) => item.status === 'Complete').length], ['Repeat opportunities', this.state.orders.filter((item) => item.repeat === 'Yes').length]])}${this.table(['Order', 'Customer', 'Offer', 'Qty', 'Revenue', 'Cash cost', 'Tax reserve', 'Pocket cash', 'Founder hrs', 'Status', 'Repeat?', 'Referral?', ''], rows, 'wide')}`)
  }

  private renderContent(): void {
    this.setView('content', `<div class="pagehead"><div><h2>Content Engine</h2><p>Show evidence, failure, customer reaction, time saved, and cash movement.</p></div><button class="btn green" data-action="add-content">Add Content</button></div><div class="cards">${this.state.content.map((item) => `<article class="card item-card"><div class="eyebrow">${escapeHtml(item.series)}</div><h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.platform)}</p><div class="meta"><span class="pill ${item.status === 'Published' ? 'done' : 'open'}">${escapeHtml(item.status)}</span><span class="pill">${item.inquiries} inquiries</span><span class="pill">${money(item.revenue)}</span></div><select data-update="content" data-id="${item.id}" data-key="status">${optionList(CONTENT_STATES, item.status)}</select><div class="footer"><small>Show outcome, not proprietary internals.</small><button class="btn danger icon-only" data-action="delete-content" data-id="${item.id}" aria-label="Delete content">x</button></div></article>`).join('')}</div>`)
  }

  private renderDecisions(): void {
    this.setView('decisions', `<div class="pagehead"><div><h2>Founder Decision Log</h2><p>Trevahhh recommends and implements. Jay decides taste, humor, public position, and final approval.</p></div><button class="btn green" data-action="add-decision">Record Decision</button></div>${this.table(['Date', 'Domain', 'Trigger', 'Founder decision', 'Why', 'Status', 'Classification', ''], this.state.decisions.map((decision) => `<tr><td>${escapeHtml(decision.date)}</td><td>${escapeHtml(decision.domain)}</td><td>${escapeHtml(decision.trigger)}</td><td><b>${escapeHtml(decision.decision)}</b></td><td>${escapeHtml(decision.why)}</td><td>${escapeHtml(decision.status)}</td><td><span class="pill ${decision.class === 'Cool' ? 'done' : decision.class === 'Hate' ? 'blocked' : 'open'}">${escapeHtml(decision.class)}</span></td><td><button class="btn danger icon-only" data-action="delete-decision" data-id="${decision.id}" aria-label="Delete decision">x</button></td></tr>`).join(''))}`)
  }

  private renderSettings(): void {
    const labels: Record<string, string> = { material: 'Material cost / unit', packaging: 'Packaging / unit', processingRate: 'Processing rate', processingFixed: 'Processing fixed fee', electricSession: 'Electricity / session', unitsSession: 'Units / session', failureReserve: 'Failure reserve', laborHour: 'Founder labor / hour', targetMargin: 'Target cash margin', taxReserve: 'Tax reserve', emergencyReserve: 'Emergency capacity reserve' }
    this.setView('settings', `<div class="pagehead"><div><h2>Settings + Data Control</h2><p>Starter values remain hypotheses until measured evidence replaces them.</p></div></div><div class="split"><section class="card section"><h3>Economics</h3><p class="desc">Used for live calculations.</p><div class="formgrid">${Object.entries(this.state.settings).map(([key, value]) => `<label class="field"><span>${escapeHtml(labels[key] ?? key)}</span><input data-setting="${key}" type="number" step="0.01" value="${value}" /></label>`).join('')}</div><button class="btn primary" data-action="save-settings">Save Settings</button></section><section class="card section"><h3>Data Control</h3><p class="desc">Local-first data stays usable offline. Cloud sync is additive and account-owned.</p><div class="today-list"><button class="btn" data-action="export">Export validated JSON backup</button><button class="btn" data-action="import">Import JSON backup</button><button class="btn" data-action="show-backups">Automatic backups</button><button class="btn" data-action="show-revisions">Revision history</button><button class="btn danger" data-action="reset">Reset local data</button></div><div class="notice gold">Reset creates a recoverable backup first and does not delete cloud records.</div></section><section class="card section" id="cloudSyncCard"></section><section class="card section" id="operationsSpineCard"></section></div>`)
  }

  private handleSyncStatusChanged(): void {
    this.renderSyncSurfaces()
    this.renderCodexFuelSurfaces()
    if (this.codexFuel.canQuery) void this.codexFuel.load().catch((error: unknown) => this.codexFuel.handleError(error))
  }

  private renderCodexFuelSurfaces(): void {
    const compact = document.getElementById('codexFuelCompact')
    if (compact) compact.innerHTML = this.codexFuel.compactHtml()
    this.setView('codex-fuel', this.codexFuel.pageHtml())
  }

  renderSyncSurfaces(): void {
    const badge = document.getElementById('syncBadge')
    if (badge) {
      const labels: Record<string, string> = { local: 'Local only', 'signed-out': 'Cloud signed out', syncing: 'Syncing', synced: 'Synced', offline: 'Offline queue', error: 'Sync issue', conflict: 'Conflict review' }
      badge.innerHTML = `<i class="dot ${this.sync.status}"></i> ${escapeHtml(labels[this.sync.status] ?? this.sync.status)}`
      badge.setAttribute('title', this.sync.statusDetail)
    }
    const card = document.getElementById('cloudSyncCard')
    if (!card) return
    const conflicts = this.sync.conflicts.length
    card.innerHTML = `<h3>Cloud Sync</h3><p class="desc">Same secure workspace on every signed-in device, with offline queueing and realtime updates.</p><div class="today-list">${this.actionRow('S', this.sync.statusDetail, this.sync.currentUser?.email ?? (this.sync.configured ? 'Signed out' : 'Supabase not configured'), `${this.sync.queueSize} pending`)}${this.actionRow('D', `${this.sync.onlineDevices} device${this.sync.onlineDevices === 1 ? '' : 's'} online`, this.sync.meta.deviceName, conflicts ? `${conflicts} conflicts` : 'No conflicts')}<button class="btn primary" data-action="sync-modal">${this.sync.currentUser ? 'Manage Cloud Sync' : this.sync.configured ? 'Sign In and Sync' : 'Configure Cloud Sync'}</button></div>`
  }

  private renderOperationsSurfaces(): void {
    this.renderJayTaskSurface()
    const card = document.getElementById('operationsSpineCard')
    if (!card) return
    const overview = this.opsStatus.overview
    const stateLabel = this.opsStatus.state === 'online' ? 'ONLINE' : this.opsStatus.state === 'checking' ? 'CHECKING' : 'OFFLINE'
    const stateClass = this.opsStatus.state === 'online' ? 'done' : this.opsStatus.state === 'checking' ? 'open' : 'blocked'
    const jobRows = overview?.jobs.length
      ? overview.jobs.map((job) => {
        const canCancel = !['succeeded', 'failed', 'cancelled', 'interrupted', 'stale'].includes(job.state)
        const artifacts = job.artifacts?.slice(0, 2) ?? []
        const artifactLabels = artifacts.length ? `Artifacts: ${artifacts.map((artifact) => artifact.label).join(', ')}` : `${job.state} / ${job.approvalState}`
        const artifactControls = artifacts.length
          ? `<div class="artifact-actions">${artifacts.map((artifact) => {
              const artifactKey = `${job.id}:${artifact.id}`
              return `<button class="btn" data-action="view-ops-artifact" data-id="${escapeHtml(artifactKey)}">View ${escapeHtml(artifact.label)}</button><button class="btn ghost" data-action="download-ops-artifact" data-id="${escapeHtml(artifactKey)}">Download</button>`
            }).join('')}</div>`
          : ''
        return `<div class="action"><div class="num">J</div><div><strong>${escapeHtml(job.normalizedObjective || job.id)}</strong><small>${escapeHtml(artifactLabels)}</small>${artifactControls}</div>${canCancel ? `<button class="btn danger" data-action="cancel-ops-job" data-id="${escapeHtml(job.id)}">Cancel</button>` : `<span class="pill open">${escapeHtml(new Date(job.updatedAt).toLocaleTimeString())}</span>`}</div>`
      }).join('')
      : this.actionRow('J', 'No recent jobs available', 'Start the local ops server to read durable job state.', 'Read only')
    const approvalRows = overview?.approvals.length
      ? overview.approvals.map((approval) => `<div class="action"><div class="num">A</div><div><strong>${escapeHtml(approval.requestedAction)}</strong><small>${escapeHtml(approval.rationale || `${approval.riskLevel} risk approval`)}</small></div><div class="approval-actions"><button class="btn" data-action="reject-ops" data-id="${escapeHtml(approval.id)}">Reject</button><button class="btn primary" data-action="approve-ops" data-id="${escapeHtml(approval.id)}">Approve</button></div></div>`).join('')
      : this.actionRow('A', 'No pending approvals', 'Approval-gated jobs will appear here before execution.', 'Clear')
    const commandRows = overview?.commands.length
      ? overview.commands.map((command) => `<div class="action"><div class="num">V</div><div><strong>${escapeHtml(command.label)}</strong><small>${escapeHtml(command.description)}</small></div><button class="btn" data-action="start-ops-preset" data-id="${escapeHtml(command.id)}">Run Check</button></div>`).join('')
      : ''
    const projectCommandRows = overview
      ? overview.projects.length
        ? overview.projects.map((project, index) => {
            const commands = overview.projectCommandsByProjectId[project.id] ?? []
            const rows = commands.length
              ? commands.map((command) => `<div class="action"><div class="num">PC</div><div><strong>${escapeHtml(command.label)}</strong><small>${escapeHtml(`${command.riskLevel} risk${command.requiresApproval ? ' / approval required' : ''} - ${command.description}`)}</small></div><button class="btn" data-action="start-project-command" data-id="${escapeHtml(`${project.id}:${command.id}`)}">Run Command</button></div>`).join('')
              : this.actionRow('PC', 'No project commands configured', 'Add allowlisted argv commands for this project.', 'Empty')
            return `<div class="action"><div class="num">P${index + 1}</div><div><strong>${escapeHtml(project.displayName)}</strong><small>${escapeHtml(project.repositoryPath)}</small></div><button class="btn" data-action="manage-project-commands" data-id="${escapeHtml(project.id)}">Manage Commands</button></div>${rows}`
          }).join('')
        : this.actionRow('P', 'No registered project', 'Register a project through the operations CLI or API.', 'Missing')
      : ''
    const artifactLibraryRow = overview
      ? `<div class="action"><div class="num">AR</div><div><strong>Artifact Library</strong><small>${escapeHtml(`${overview.artifacts.length} recorded artifact${overview.artifacts.length === 1 ? '' : 's'} available`)}</small></div><button class="btn" data-action="artifact-library">Browse Artifacts</button></div>`
      : ''
    card.innerHTML = `<div class="section-head"><div><h3>Local Operations Spine</h3><p class="desc">Reads authoritative local job state from the Windows operations API.</p></div><span class="pill ${stateClass}">${stateLabel}</span></div><div class="today-list">${this.actionRow('H', this.opsStatus.detail, overview ? `Schema ${overview.health.schemaVersion} / ${overview.health.activeJobCount} active` : 'http://127.0.0.1:8787', overview ? `${overview.health.jobCount} jobs` : 'Not connected')}${overview ? this.actionRow('C', overview.diagnostics.codex.codexCliVersion ?? (overview.diagnostics.codex.installed ? 'Codex installed' : 'Codex missing'), overview.diagnostics.bridge.paired ? 'Bridge paired' : 'Bridge unpaired', overview.diagnostics.bridge.configured ? 'Configured' : 'Not configured') : ''}${artifactLibraryRow}${projectCommandRows}${commandRows}${approvalRows}${jobRows}<button class="btn" data-action="refresh-ops">Refresh Operations Status</button></div>`
  }

  private renderJayTaskSurface(): void {
    const surface = document.getElementById('jayTaskSurface')
    if (!surface) return
    const tasks = this.opsStatus.overview?.tasks?.length
      ? this.opsStatus.overview.tasks
      : (this.opsStatus.overview?.approvals ?? []).map((approval) => ({
          ...approval,
          source: 'approval',
          action: 'approval',
          actionLabel: 'Respond'
        }))
    if (tasks.length) {
      const task = tasks[0]!
      const extra = tasks.length > 1 ? `${tasks.length - 1} more task${tasks.length === 2 ? '' : 's'} waiting` : task.source === 'release-gate' ? 'Release gate blocker' : 'Newest task from Trevahhh Ops'
      const actions = task.source === 'approval'
        ? `<button class="btn ghost" data-action="reject-ops" data-id="${escapeHtml(task.id)}">Reject Task</button><button class="btn primary" data-action="approve-ops" data-id="${escapeHtml(task.id)}">Approve Task</button>`
        : task.action === 'complete-task'
          ? `<button class="btn primary" data-action="complete-jay-task" data-id="${escapeHtml(task.id)}">${escapeHtml(task.actionLabel ?? 'Mark Complete')}</button>`
          : `<button class="btn primary" data-action="${task.action === 'signing' ? 'signing-instructions' : 'refresh-ops'}" data-id="${escapeHtml(task.id)}">${escapeHtml(task.actionLabel ?? 'Review Task')}</button>`
      surface.dataset.state = 'attention'
      surface.innerHTML = `
        <div class="jay-task-copy">
          <span class="eyebrow">Jay task needed</span>
          <strong>${escapeHtml(task.requestedAction)}</strong>
          <small>${escapeHtml(task.rationale || `${task.riskLevel} risk approval`)} · ${escapeHtml(extra)}</small>
        </div>
        <div class="jay-task-actions">
          ${actions}
          <button class="btn" data-view="settings">Open Settings</button>
        </div>`
      return
    }
    if (this.opsStatus.state === 'checking') {
      surface.dataset.state = 'checking'
      surface.innerHTML = `<div class="jay-task-copy"><span class="eyebrow">Jay task queue</span><strong>Checking for tasks...</strong><small>Pending approvals will appear here automatically.</small></div><button class="btn" data-action="refresh-ops">Refresh</button>`
      return
    }
    if (this.opsStatus.state === 'offline') {
      surface.dataset.state = 'offline'
      surface.innerHTML = `<div class="jay-task-copy"><span class="eyebrow">Jay task queue</span><strong>Tasks unavailable</strong><small>${escapeHtml(this.opsStatus.detail)}</small></div><button class="btn" data-action="refresh-ops">Retry</button>`
      return
    }
    surface.dataset.state = 'clear'
    surface.innerHTML = `<div class="jay-task-copy"><span class="eyebrow">Jay task queue</span><strong>No Jay tasks pending</strong><small>Approval-gated work will update this HTML the moment Trevahhh Ops needs Jay.</small></div><button class="btn" data-view="settings">Open Settings</button>`
  }

  private findOperationsTask(id?: string): OperationsTask | undefined {
    if (!id) return undefined
    return this.opsStatus.overview?.tasks.find((task) => task.id === id)
  }

  private openSigningInstructionsModal(taskId?: string): void {
    const task = this.findOperationsTask(taskId)
    const details = task?.details ?? {}
    const packageRows = [
      details.packageId ? this.actionRow('PKG', 'Current package', String(details.packageId), String(details.signingStatus ?? 'unsigned')) : '',
      details.zipSha256 ? this.actionRow('ZIP', 'Current ZIP SHA-256', String(details.zipSha256), 'Artifact') : '',
      details.manifestPath ? this.actionRow('MAN', 'Manifest path', String(details.manifestPath), 'Evidence') : '',
      details.packageDir ? this.actionRow('DIR', 'Package directory', String(details.packageDir), 'Local') : ''
    ].join('')
    const trustedPreflight = details.trustedPreflightCommand ?? 'npm run check:signing -- --require-trusted-cert --json --cert-thumbprint "CERT_THUMBPRINT"'
    const signedPackage = details.signedPackageCommand ?? 'npm run release:complete-signing -- --json --apply --confirm-release-signing --output .release/signed --evidence-dir .release/production-evidence --cert-thumbprint "CERT_THUMBPRINT"'
    const releaseGate = details.releaseGateCommand ?? 'npm run release:gate -- --package-dir .release/signed --evidence-dir .release/production-evidence --json'
    this.openModal(
      'Signing Required',
      `<div class="notice gold">Public release is blocked until the Windows package is signed with a trusted current-user code-signing certificate. This app will not create certificates or sign artifacts automatically.</div>
      ${packageRows ? `<div class="today-list">${packageRows}</div>` : ''}
      <div class="today-list">
        ${this.actionRow('1', 'Configure certificate thumbprint', 'Set TREV_OS_SIGNING_CERT_THUMBPRINT to a trusted CurrentUser code-signing certificate thumbprint.', 'Jay')}
        ${this.actionRow('2', 'Verify trusted signing prerequisites', String(trustedPreflight), 'Gate')}
        ${this.actionRow('3', 'Build signed package', String(signedPackage), 'Release')}
        ${this.actionRow('4', 'Run strict release gate', String(releaseGate), 'Deploy gate')}
      </div>`,
      '<button class="btn" data-action="close-modal">Close</button>'
    )
  }

  private async refreshOperationsStatus(): Promise<void> {
    this.opsStatus = { state: 'checking', detail: 'Checking local operations server...', overview: this.opsStatus.overview }
    this.renderOperationsSurfaces()
    try {
      const overview = await this.ops.readOverview()
      this.opsStatus = { state: 'online', detail: 'Local operations API is reachable.', overview }
    } catch (error) {
      this.opsStatus = {
        state: 'offline',
        detail: error instanceof Error && error.name === 'AbortError' ? 'Local operations API timed out.' : 'Local operations API is not reachable.',
        overview: null
      }
    }
    this.renderOperationsSurfaces()
  }

  private async respondOperationsApproval(id: string, response: 'approved' | 'rejected'): Promise<void> {
    try {
      await this.ops.respondApproval(id, response)
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private async completeJayTask(id: string): Promise<void> {
    try {
      await this.ops.completeJayTask(id)
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private async startOperationsPreset(id: string): Promise<void> {
    try {
      await this.ops.startPreset(id)
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private async cancelOperationsJob(id: string): Promise<void> {
    try {
      await this.ops.cancelJob(id)
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private parseArtifactKey(key: string): { jobId: string; artifactId: string } {
    const separator = key.indexOf(':')
    if (separator <= 0 || separator === key.length - 1) throw new Error('Invalid artifact reference.')
    return { jobId: key.slice(0, separator), artifactId: key.slice(separator + 1) }
  }

  private async openOperationsArtifact(key: string): Promise<void> {
    try {
      const { jobId, artifactId } = this.parseArtifactKey(key)
      const result = await this.ops.readArtifactContent(jobId, artifactId)
      const pretty = JSON.stringify(result.content, null, 2)
      this.openModal(
        `Artifact: ${result.artifact.label}`,
        `<div class="artifact-meta">${this.metricStrip([
          ['Type', result.artifact.artifactType],
          ['Bytes', result.artifact.sizeBytes],
          ['SHA-256', result.artifact.sha256.slice(0, 12)],
          ['Created', new Date(result.artifact.createdAt).toLocaleString()]
        ])}</div><pre class="artifact-json">${escapeHtml(pretty)}</pre>`,
        `<button class="btn" data-action="close-modal">Close</button><button class="btn primary" data-action="download-ops-artifact" data-id="${escapeHtml(key)}">Download JSON</button>`
      )
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private async downloadOperationsArtifact(key: string): Promise<void> {
    try {
      const { jobId, artifactId } = this.parseArtifactKey(key)
      const result = await this.ops.readArtifactContent(jobId, artifactId)
      const body = `${JSON.stringify(result.content, null, 2)}\n`
      const url = URL.createObjectURL(new Blob([body], { type: 'application/json' }))
      const anchor = document.createElement('a')
      anchor.href = url
      const safeLabel = result.artifact.label.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'artifact'
      anchor.download = `trevahhh-artifact-${safeLabel}-${artifactId.slice(0, 8)}.json`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private openArtifactLibraryModal(): void {
    const artifacts = this.opsStatus.overview?.artifacts ?? []
    const body = artifacts.length
      ? `<div class="today-list">${artifacts.map((artifact) => {
          const artifactKey = `${artifact.jobId}:${artifact.id}`
          return `<div class="action"><div class="num">AR</div><div><strong>${escapeHtml(artifact.label)}</strong><small>${escapeHtml(`${artifact.artifactType} / ${artifact.job.normalizedObjective} / ${new Date(artifact.createdAt).toLocaleString()}`)}</small></div><div class="approval-actions"><button class="btn" data-action="view-ops-artifact" data-id="${escapeHtml(artifactKey)}">View</button><button class="btn ghost" data-action="download-ops-artifact" data-id="${escapeHtml(artifactKey)}">Download</button></div></div>`
        }).join('')}</div>`
      : '<div class="empty">No artifacts have been recorded yet.</div>'
    this.openModal('Artifact Library', body, '<button class="btn" data-action="close-modal">Close</button>')
  }

  private openProjectCommandsModal(projectId: string): void {
    const project = this.opsStatus.overview?.projects.find((item) => item.id === projectId)
    if (!project) {
      alert('Project is not available in the current operations overview.')
      return
    }
    const body = `<div class="notice gold">Commands must be named entries with a command string and args array. High-risk entries require approval before execution.</div><label class="field wide-field"><span>Command allowlist JSON</span><textarea id="projectCommandsJson" class="code-textarea">${escapeHtml(JSON.stringify(project.defaultCommands ?? {}, null, 2))}</textarea></label>`
    this.openModal(`Commands: ${project.displayName}`, body, `<button class="btn" data-action="close-modal">Cancel</button><button class="btn primary" data-action="save-project-commands" data-id="${escapeHtml(project.id)}">Save Commands</button>`)
  }

  private async saveProjectCommands(projectId: string): Promise<void> {
    try {
      const text = (document.getElementById('projectCommandsJson') as HTMLTextAreaElement | null)?.value ?? '{}'
      const commands = JSON.parse(text)
      await this.ops.updateProjectCommands(projectId, commands)
      this.closeModal()
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private parseProjectCommandKey(key: string): { projectId: string; commandId: string } {
    const separator = key.indexOf(':')
    if (separator <= 0 || separator === key.length - 1) throw new Error('Invalid project command reference.')
    return { projectId: key.slice(0, separator), commandId: key.slice(separator + 1) }
  }

  private async startProjectCommand(key: string): Promise<void> {
    try {
      const { projectId, commandId } = this.parseProjectCommandKey(key)
      await this.ops.startProjectCommand(projectId, commandId)
      await this.refreshOperationsStatus()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private inputCell(collection: string, id: string, key: string, value: unknown, type = 'text', step = '1'): string {
    return `<td><input data-update="${collection}" data-id="${id}" data-key="${key}" type="${type}" step="${step}" value="${escapeHtml(value)}" /></td>`
  }

  private metricStrip(items: Array<[string, string | number]>): string {
    return `<div class="metric-strip">${items.map((item) => `<div class="metric-mini"><span>${escapeHtml(item[0])}</span><b>${escapeHtml(item[1])}</b></div>`).join('')}</div>`
  }

  private table(headers: string[], rows: string, extraClass = ''): string {
    return `<div class="table-wrap"><table class="${extraClass}"><thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table></div>`
  }

  private setView(view: ViewName, html: string): void {
    const el = document.getElementById(`view-${view}`)
    if (el) el.innerHTML = html
  }

  private switchView(view: ViewName): void {
    this.activeView = view
    document.querySelectorAll('.view').forEach((el) => el.classList.toggle('active', el.id === `view-${view}`))
    document.querySelectorAll('[data-view]').forEach((el) => el.classList.toggle('active', (el as HTMLElement).dataset.view === view))
    const topTitle = document.getElementById('topTitle')
    if (topTitle) topTitle.textContent = viewLabels[view]
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  private updateField(collection: string, id: string, key: string, value: string): void {
    if (collection === 'manualChecks') {
      const input = document.querySelector<HTMLInputElement>(`input[data-update="manualChecks"][data-key="${key}"]`)
      this.state.manualChecks[key as keyof AppState['manualChecks']] = Boolean(input?.checked)
      this.save('manual-check')
      return
    }
    const targetCollection = (this.state as unknown as Record<string, unknown>)[collection]
    if (!Array.isArray(targetCollection)) return
    const item = targetCollection.find((entry) => (entry as { id?: string }).id === id) as Record<string, unknown> | undefined
    if (!item) return
    const current = item[key]
    item[key] = typeof current === 'number' ? num(value) : value
    this.save('field-update')
  }

  private deleteItem(collection: CollectionName, id?: string): void {
    if (!id || !confirm(`Delete this ${collection} record?`)) return
    const current = (this.state as unknown as Record<string, unknown>)[collection]
    if (!Array.isArray(current)) return
    ;(this.state as unknown as Record<string, unknown>)[collection] = current.filter((item) => (item as { id?: string }).id !== id)
    this.save('delete')
  }

  private addCapacityUnit(): void {
    const unit: CapacityUnit = { id: createId(), date: todayISO(), design: '', material: this.state.settings.material, art: 0, setup: 0, front: 0, flip: 0, back: 0, finish: 0, package: 0, machine: 0, pass: '', defect: '', notes: '' }
    this.state.capacity.push(unit)
    this.save('add-capacity')
  }

  private resetSprint(): void {
    if (!confirm('Reset every sprint status and evidence field?')) return
    createAutomaticBackup(this.state, 'before-sprint-reset')
    this.state.sprint = initialState().sprint
    this.save('reset-sprint')
  }

  private resetAll(): void {
    if (!confirm('Reset local data on this device? A recoverable backup will be created first.')) return
    createAutomaticBackup(this.state, 'before-reset')
    this.state = initialState()
    recordRevision({ key: 'workspace:local', collection: 'settings', recordId: 'singleton', operation: 'reset', deviceId: this.sync.meta.deviceId, payload: this.state })
    this.save('reset')
  }

  private saveSettings(): void {
    document.querySelectorAll<HTMLInputElement>('[data-setting]').forEach((input) => {
      this.state.settings[input.dataset.setting as keyof AppState['settings']] = num(input.value)
    })
    this.save('settings')
  }

  private openOfferModal(existing?: Offer): void {
    const offer = existing ?? { id: '', name: '', buyer: '', price: 0, qty: 1, status: 'Test', rights: 'Jay-owned original', repeat: '', line: '' }
    this.openForm('Offer', [['Name', 'name', offer.name], ['Buyer', 'buyer', offer.buyer], ['Price', 'price', offer.price, 'number'], ['Quantity', 'qty', offer.qty, 'number'], ['Status', 'status', offer.status], ['Artwork rights', 'rights', offer.rights], ['Repeat path', 'repeat', offer.repeat], ['Sales line', 'line', offer.line, 'textarea']], (values) => {
      if (existing) Object.assign(existing, values)
      else this.state.offers.push({ ...(values as Omit<Offer, 'id'>), id: createId() })
      this.save('offer')
    })
  }

  private openLeadModal(): void {
    this.openForm('New Lead', [['Business', 'business', ''], ['Segment', 'segment', ''], ['Offer angle', 'angle', ''], ['Contact', 'contact', ''], ['Next action', 'next', 'Make first contact'], ['Next date', 'nextDate', '', 'date'], ['Potential', 'potential', 'High']], (values) => {
      this.state.leads.push({ ...(values as Omit<Lead, 'id' | 'rank' | 'status' | 'notes'>), id: createId(), rank: this.state.leads.length + 1, status: 'Not Contacted', notes: '' })
      this.save('lead')
    })
  }

  private openOrderModal(): void {
    this.openForm('New Order', [['Customer', 'customer', ''], ['Offer', 'offer', 'Paid B2B Pilot'], ['Quantity', 'qty', 3, 'number'], ['Unit price', 'unitPrice', 59.67, 'number'], ['Material cost', 'material', 3.75, 'number'], ['Packaging cost', 'packaging', 3, 'number'], ['Other cash cost', 'other', 0, 'number'], ['Remake cost', 'remake', 0, 'number'], ['Founder hours', 'hours', 3, 'number'], ['Status', 'status', 'Deposit Paid']], (values) => {
      this.state.orders.push({ ...(values as Omit<Order, 'id' | 'orderNo' | 'repeat' | 'referral'>), id: createId(), orderNo: `ORD-${Date.now().toString().slice(-7)}`, repeat: 'No', referral: 'No' })
      this.save('order')
    })
  }

  private openContentModal(): void {
    this.openForm('New Content', [['Series', 'series', 'Building Jay Carr'], ['Title / hook', 'title', ''], ['Platform', 'platform', 'TikTok + YouTube Shorts'], ['Status', 'status', 'Idea'], ['Notes', 'notes', '', 'textarea']], (values) => {
      this.state.content.push({ ...(values as Omit<ContentItem, 'id' | 'inquiries' | 'revenue'>), id: createId(), inquiries: 0, revenue: 0 })
      this.save('content')
    })
  }

  private openDecisionModal(): void {
    this.openForm('Founder Decision', [['Date', 'date', todayISO(), 'date'], ['Domain', 'domain', ''], ['Evidence / trigger', 'trigger', ''], ['Decision', 'decision', '', 'textarea'], ['Why', 'why', '', 'textarea'], ['Status', 'status', 'Active'], ['Classification', 'class', 'Cool']], (values) => {
      this.state.decisions.unshift({ ...(values as Omit<Decision, 'id'>), id: createId() })
      this.save('decision')
    })
  }

  private openForm(title: string, fields: Array<[string, string, string | number, string?]>, onSave: (values: Record<string, string | number>) => void): void {
    const body = `<div class="formgrid">${fields.map((field) => `<label class="field ${field[3] === 'textarea' ? 'wide-field' : ''}"><span>${escapeHtml(field[0])}</span>${field[3] === 'textarea' ? `<textarea data-form-key="${field[1]}">${escapeHtml(field[2])}</textarea>` : `<input data-form-key="${field[1]}" type="${field[3] ?? 'text'}" value="${escapeHtml(field[2])}" />`}</label>`).join('')}</div>`
    this.modalSaveHandler = () => {
      const values: Record<string, string | number> = {}
      document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-form-key]').forEach((input) => {
        values[input.dataset.formKey ?? ''] = input.type === 'number' ? num(input.value) : input.value
      })
      onSave(values)
      this.closeModal()
    }
    this.openModal(title, body, '<button class="btn" data-action="close-modal">Cancel</button><button class="btn primary" data-action="modal-save">Save</button>')
  }

  private markToday(): void {
    this.openForm("Record Today's Result", [['Date', 'date', todayISO(), 'date'], ['Revenue action', 'revenue', ''], ['Production action', 'production', ''], ['System action', 'system', ''], ['Evidence / result', 'result', '', 'textarea'], ['Next step', 'next', ''], ['Founder hours', 'hours', 1, 'number']], (values) => {
      this.state.daily.unshift({ ...(values as Omit<DailyRecord, 'id'>), id: createId() })
      this.save('daily')
    })
  }

  private openOneHour(): void {
    const action = nextAction(this.state)
    this.openModal('One-Hour Founder Mode', `<div class="quote"><b>Stop condition:</b> Do not open a new project. The hour succeeds only when evidence for the current gate changes.</div><div class="timer" id="timer">60:00</div><div class="agenda"><div class="slot"><div class="time">0-5 min</div><div><b>Orient</b><br>Open the exact record required for today's gate.</div></div><div class="slot"><div class="time">5-30 min</div><div><b>${escapeHtml(action.type)}</b><br>${escapeHtml(action.text)}</div></div><div class="slot"><div class="time">30-50 min</div><div><b>Complete supporting execution</b><br>Production if the gate needs proof; outreach if the gate needs cash.</div></div><div class="slot"><div class="time">50-60 min</div><div><b>Record evidence</b><br>Update the system, capture the result, and set one dated next action.</div></div></div>`, '<button class="btn" data-action="close-modal">Close</button><button class="btn primary" data-action="start-timer">Start 60 Minutes</button>')
  }

  private startTimer(): void {
    if (this.timerHandle) window.clearInterval(this.timerHandle)
    let sec = 3600
    this.timerHandle = window.setInterval(() => {
      sec -= 1
      const timer = document.getElementById('timer')
      if (timer) timer.textContent = `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`
      if (sec <= 0 && this.timerHandle) {
        window.clearInterval(this.timerHandle)
        alert('One-hour mode complete. Record the evidence and next action.')
      }
    }, 1000)
  }

  private goToNextAction(): void {
    const action = nextAction(this.state)
    this.switchView(action.view as ViewName)
    if (action.manual) alert(action.text)
  }

  private openSyncModal(): void {
    const config = this.sync.config()
    const conflicts = this.sync.conflicts
    let body = ''
    let foot = '<button class="btn" data-action="close-modal">Close</button>'
    if (!this.sync.configured) {
      body = `<div class="notice gold">One-time setup uses only the Supabase publishable browser key. Service-role keys are rejected.</div><div class="formgrid"><label class="field wide-field"><span>Supabase project URL</span><input id="syncUrl" value="${escapeHtml(config.supabaseUrl)}" placeholder="https://PROJECT.supabase.co" /></label><label class="field wide-field"><span>Publishable key</span><input id="syncKey" type="password" value="${escapeHtml(config.supabasePublishableKey)}" placeholder="sb_publishable_..." /></label><label class="field wide-field"><span>Hosted app URL</span><input id="syncAppUrl" value="${escapeHtml(config.appUrl)}" placeholder="https://trevahhh-os.pages.dev/" /></label></div>`
      foot = '<button class="btn" data-action="close-modal">Cancel</button><button class="btn primary" data-action="save-sync-config">Save and Connect</button>'
    } else if (!this.sync.currentUser) {
      body = `<div class="notice gold">Sign in with the same account on every device. Sessions can remain active on multiple devices.</div><div class="formgrid"><label class="field wide-field"><span>Email</span><input id="syncEmail" type="email" autocomplete="email" /></label><label class="field wide-field"><span>Password</span><input id="syncPassword" type="password" autocomplete="current-password" /></label></div><div class="today-list"><button class="btn primary" data-action="send-magic-link">Email sign-in link</button><button class="btn" data-action="password-sign-in">Sign in with password</button><button class="btn" data-action="password-sign-up">Create account with password</button></div>`
    } else {
      body = `${this.metricStrip([['Account', this.sync.currentUser.email ?? 'Signed in'], ['Pending', this.sync.queueSize], ['Conflicts', conflicts.length], ['Online devices', this.sync.onlineDevices]])}<label class="field"><span>This device name</span><input id="deviceName" value="${escapeHtml(this.sync.meta.deviceName)}" /></label><div class="today-list"><button class="btn primary" data-action="sync-now">Sync Now</button><button class="btn" data-action="merge-local">Merge This Device Into Cloud</button><button class="btn" data-action="save-device">Save Device Name</button><button class="btn danger" data-action="sign-out">Sign Out on This Device</button></div>${conflicts.length ? `<h4>Conflicts</h4><div class="conflicts">${conflicts.map((conflict) => `<div class="conflict"><b>${escapeHtml(conflict.collection)} / ${escapeHtml(conflict.recordId)}</b><small>Remote changed after this device's offline base.</small><div><button class="btn" data-action="conflict-remote" data-id="${escapeHtml(conflict.key)}">Use Cloud</button><button class="btn primary" data-action="conflict-local" data-id="${escapeHtml(conflict.key)}">Keep This Device</button></div></div>`).join('')}</div>` : '<div class="notice gold">No unresolved conflicts. Normal use remains automatic.</div>'}`
    }
    this.openModal('Seamless Device Sync', body, foot)
  }

  private async saveSyncConfiguration(): Promise<void> {
    try {
      await this.sync.saveConfiguration({
        supabaseUrl: (document.getElementById('syncUrl') as HTMLInputElement | null)?.value.trim() ?? '',
        supabasePublishableKey: (document.getElementById('syncKey') as HTMLInputElement | null)?.value.trim() ?? '',
        appUrl: (document.getElementById('syncAppUrl') as HTMLInputElement | null)?.value.trim() ?? '',
        workspaceName: this.sync.config().workspaceName
      })
      this.openSyncModal()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private async syncAuth(mode: 'magic' | 'login' | 'signup'): Promise<void> {
    const email = (document.getElementById('syncEmail') as HTMLInputElement | null)?.value.trim() ?? ''
    const password = (document.getElementById('syncPassword') as HTMLInputElement | null)?.value ?? ''
    try {
      if (!email) throw new Error('Enter an email address.')
      if (mode === 'magic') await this.sync.sendMagicLink(email)
      if (mode === 'login') await this.sync.passwordSignIn(email, password)
      if (mode === 'signup') {
        if (password.length < 8) throw new Error('Use a password with at least 8 characters.')
        await this.sync.passwordSignUp(email, password)
      }
      alert(mode === 'magic' ? 'Sign-in link sent.' : 'Authentication request completed.')
      this.closeModal()
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error))
    }
  }

  private openBackupsModal(): void {
    const backups = listAutomaticBackups().slice().reverse()
    this.openModal('Automatic Backups', backups.length ? `<div class="today-list">${backups.map((backup) => `<div class="action"><div class="num">B</div><div><strong>${escapeHtml(backup.reason)}</strong><small>${new Date(backup.timestamp).toLocaleString()}</small></div><span class="pill">${backup.state.leads.length} leads</span></div>`).join('')}</div>` : '<div class="empty">No automatic backups yet.</div>', '<button class="btn" data-action="close-modal">Close</button>')
  }

  private openRevisionsModal(): void {
    const revisions = listRevisions().slice(-80).reverse()
    this.openModal('Revision History', revisions.length ? `<div class="today-list">${revisions.map((revision) => `<div class="action"><div class="num">R</div><div><strong>${escapeHtml(revision.operation)} - ${escapeHtml(revision.collection)}</strong><small>${escapeHtml(revision.recordId)} / ${new Date(revision.timestamp).toLocaleString()}</small></div><span class="pill">${escapeHtml(revision.deviceId.slice(0, 8))}</span></div>`).join('')}</div>` : '<div class="empty">No revisions recorded yet.</div>', '<button class="btn" data-action="close-modal">Close</button>')
  }

  private async importData(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return
    try {
      createAutomaticBackup(this.state, 'before-import')
      const envelope = parseAndValidateBackup(await file.text())
      this.state = envelope.state
      recordRevision({ key: 'workspace:import', collection: 'settings', recordId: 'singleton', operation: 'import', deviceId: this.sync.meta.deviceId, payload: envelope })
      this.save('import')
      alert('Backup imported and validated.')
    } catch (error) {
      alert(`Import failed: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      ;(event.target as HTMLInputElement).value = ''
    }
  }

  private openModal(title: string, body: string, foot: string): void {
    const titleEl = document.getElementById('modalTitle')
    const bodyEl = document.getElementById('modalBody')
    const footEl = document.getElementById('modalFoot')
    if (titleEl) titleEl.textContent = title
    if (bodyEl) bodyEl.innerHTML = body
    if (footEl) footEl.innerHTML = foot
    document.getElementById('modalBackdrop')?.classList.add('open')
  }

  private closeModal(): void {
    document.getElementById('modalBackdrop')?.classList.remove('open')
    this.modalSaveHandler = null
  }

  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) return
    if (!(location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1')) return
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js')
      if (registration.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    } catch (error) {
      logger.warn('Service worker registration failed', { error: error instanceof Error ? error.message : String(error) })
    }
  }
}
